import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ImageUp, Loader2, Save, Trash2 } from "lucide-react";
import { RichEmailEditor } from "@/components/t4r/email/RichEmailEditor";
import { EmailPreviewFrame } from "@/components/t4r/email/EmailPreviewFrame";
import { useEmailPreview } from "@/components/t4r/email/useEmailPreview";

const SAMPLE = { subject: "Example: your Retrofit update", body_html: "<p>Hi {first_name},</p><p>This is how your group emails will look with this design.</p>", cta_text: "Example button", cta_url: "https://example.com" };
const COLOURS = [["accent", "Accent (line, button, links)"], ["header_bg", "Header background"], ["background", "Page background"], ["text_color", "Text"]];
const FIELDS = ["logo_width", "accent", "header_bg", "background", "text_color", "footer_html", "optout_html"];
const HEX = /^#[0-9a-fA-F]{6}$/;

const ColourField = ({ k, label, value, onChange }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1 flex items-center gap-2">
      <input type="color" value={HEX.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-9 w-10 cursor-pointer rounded border border-border bg-white p-0.5" data-testid={`design-${k}-picker`} />
      <Input value={value} maxLength={7} onChange={(e) => onChange(e.target.value)} className={`h-9 font-mono text-xs ${HEX.test(value) ? "" : "border-red-400"}`} data-testid={`design-${k}`} />
    </div>
  </div>
);

const LogoField = ({ design, setDesign, width, setWidth }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/lms/admin/email-design/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setDesign((d) => ({ ...d, logo_url: data.logo_url })); toast.success("Logo uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm("Remove the logo? The text logo will be used instead.")) return;
    try { await api.delete("/lms/admin/email-design/logo"); setDesign((d) => ({ ...d, logo_url: "" })); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="design-logo">
      <Label className="text-xs">Logo</Label>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="design-logo-file" />
        <Button size="sm" variant="outline" className="gap-1" disabled={busy} onClick={() => input.current?.click()} data-testid="design-logo-upload">{busy ? <Loader2 size={12} className="animate-spin" /> : <ImageUp size={12} />} {design.logo_url ? "Replace logo" : "Upload logo"}</Button>
        {design.logo_url && <Button size="sm" variant="ghost" className="gap-1 text-red-600" onClick={remove} data-testid="design-logo-remove"><Trash2 size={12} /> Remove</Button>}
        <span className="text-[11px] text-muted-foreground">{design.logo_url ? "Uploaded" : `None: using the text logo "${design.wordmark}"`} · PNG, JPG or WebP, under 2 MB · logo changes save straight away</span>
      </div>
      {design.logo_url && (
        <div className="flex items-center gap-3">
          <span className="w-16 text-[11px] text-muted-foreground">Size {width}px</span>
          <Slider value={[width]} min={60} max={400} step={10} onValueChange={([v]) => setWidth(v)} className="flex-1" data-testid="design-logo-width" />
        </div>
      )}
    </div>
  );
};

export const EmailDesignDialog = ({ open, onClose }) => {
  const [design, setDesign] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDesign(null); api.get("/lms/admin/email-design").then(({ data }) => setDesign(data)).catch(() => toast.error("Couldn't load the email design")); } }, [open]);
  const set = (k) => (v) => setDesign((d) => ({ ...d, [k]: v }));
  const valid = design && COLOURS.every(([k]) => HEX.test(design[k]));
  const payload = valid ? Object.fromEntries(FIELDS.map((k) => [k, design[k]])) : null;
  const [preview, loading] = useEmailPreview(SAMPLE, "", payload);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/lms/admin/email-design", payload); setDesign(data); toast.success("Email design saved. All T4R group emails now use it."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[1280px] max-h-[92vh] overflow-y-auto grid-cols-[minmax(0,1fr)]" data-testid="email-design-dialog">
        <DialogHeader>
          <DialogTitle>Email design</DialogTitle>
          <DialogDescription className="text-xs">One shared look for every T4R group email and template. Changes show in the preview straight away and apply once you save.</DialogDescription>
        </DialogHeader>
        {!design ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div> : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_660px]">
            <div className="min-w-0 space-y-3">
              <LogoField design={design} setDesign={setDesign} width={design.logo_width} setWidth={set("logo_width")} />
              <div className="grid gap-3 rounded-lg border border-border p-3 sm:grid-cols-2" data-testid="design-colours">
                {COLOURS.map(([k, l]) => <ColourField key={k} k={k} label={l} value={design[k]} onChange={set(k)} />)}
              </div>
              <div data-testid="design-footer"><Label className="text-xs">Footer (leave empty to hide)</Label><RichEmailEditor value={design.footer_html} onChange={set("footer_html")} minHeight="min-h-[110px]" /></div>
              <div data-testid="design-optout"><Label className="text-xs">Opt-out line (shown under every message; leave empty to hide)</Label><RichEmailEditor value={design.optout_html} onChange={set("optout_html")} minHeight="min-h-[70px]" /></div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">{design.updated_by ? `Last saved by ${design.updated_by}` : "Not customised yet: showing the defaults."}</p>
                <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" disabled={!valid || busy} onClick={save} data-testid="design-save">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save design</Button>
              </div>
            </div>
            <div className="min-h-[560px] min-w-0"><EmailPreviewFrame preview={preview} loading={loading} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
